import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import axios from "axios";

const API = window.location.origin;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("La password deve avere almeno 6 caratteri"); return; }
    if (password !== confirm) { toast.error("Le password non corrispondono"); return; }
    setIsLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, password });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel reset della password");
    } finally {
      setIsLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#f5f7fa] flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white rounded-3xl p-8 shadow-md text-center">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Password aggiornata!</h2>
          <p className="text-slate-500 text-sm mb-6">Ora puoi accedere con la nuova password.</p>
          <Button onClick={() => navigate("/login")} className="bg-[#3B42DD] hover:bg-[#2f36b8] text-white rounded-xl">
            Vai al login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f7fa] flex items-center justify-center px-4">
      <Toaster position="top-right" theme="light" />
      <div className="w-full max-w-md">
        <div className="bg-[#3B42DD] rounded-3xl px-8 py-10 pb-16 relative">
          <h1 className="text-2xl font-semibold text-white mb-1">Nuova Password</h1>
          <p className="text-white/70 text-sm">Imposta la tua nuova password</p>
          <div className="absolute bottom-0 left-0 right-0 h-12 bg-[#3B42DD]" />
        </div>
        <div className="bg-white rounded-3xl p-8 shadow-md -mt-8 relative z-10">
          <form onSubmit={handleReset} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Nuova Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Almeno 6 caratteri" className="pl-10 pr-10 h-12 bg-[#f8f9fb] border-slate-200 rounded-xl" data-testid="reset-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Conferma Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input type={showPassword ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Ripeti la password" className="pl-10 h-12 bg-[#f8f9fb] border-slate-200 rounded-xl" data-testid="reset-confirm-input" />
              </div>
            </div>
            <Button type="submit" disabled={isLoading} className="w-full h-12 bg-[#3B42DD] hover:bg-[#2f36b8] text-white rounded-xl font-semibold" data-testid="reset-submit">
              {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Salva Password"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
